/**
 * Debug Configuration
 * Central configuration for all debugging features
 *
 * To disable all debugging in production:
 * Set NODE_ENV to 'production' or manually set flags to false
 */

interface DebugConfig {
  // Master switches
  enableAllLogs: boolean;
  enableConsoleDebugging: boolean;
  enableLoggerDebugging: boolean;

  // Category-specific flags
  categories: {
    app: boolean;
    api: boolean;
    auth: boolean;
    router: boolean;
    component: boolean;
    lifecycle: boolean;
    network: boolean;
    redux: boolean;
    performance: boolean;
    event: boolean;
  };

  // Component-specific flags
  components: {
    Api: boolean;
    App: boolean;
    AuthGuard: boolean;
    FlowComponent: boolean;
    ConnectionComponent: boolean;
    [key: string]: boolean;
  };
}

// Default configuration based on environment
const isDevelopment = process.env.NODE_ENV === 'development';

const debugConfig: DebugConfig = {
  // Master switches - can be toggled globally
  enableAllLogs: isDevelopment, // Set to false to disable ALL logging
  enableConsoleDebugging: isDevelopment, // Controls direct console.log statements
  enableLoggerDebugging: isDevelopment, // Controls logger utility output

  // Category-specific flags
  categories: {
    app: true,
    api: true,
    auth: true,
    router: true,
    component: true,
    lifecycle: true,
    network: true,
    redux: true,
    performance: true,
    event: true,
  },

  // Component-specific flags
  components: {
    Api: true,
    App: true,
    AuthGuard: true,
    FlowComponent: true,
    ConnectionComponent: true,
  },
};

/**
 * Check if console debugging is enabled
 */
export function isConsoleDebugEnabled(): boolean {
  return debugConfig.enableAllLogs && debugConfig.enableConsoleDebugging;
}

/**
 * Check if logger debugging is enabled
 */
export function isLoggerDebugEnabled(): boolean {
  return debugConfig.enableAllLogs && debugConfig.enableLoggerDebugging;
}

/**
 * Check if logging is enabled for a specific category
 */
export function isCategoryEnabled(category: keyof DebugConfig['categories']): boolean {
  return debugConfig.enableAllLogs && debugConfig.categories[category];
}

/**
 * Check if logging is enabled for a specific component
 */
export function isComponentEnabled(componentName: string): boolean {
  return debugConfig.enableAllLogs && (debugConfig.components[componentName] ?? false);
}

/**
 * Conditional console.log - only logs if console debugging is enabled
 */
export function debugLog(...args: any[]): void {
  if (isConsoleDebugEnabled()) {
    console.log(...args);
  }
}

/**
 * Conditional console.warn - only logs if console debugging is enabled
 */
export function debugWarn(...args: any[]): void {
  if (isConsoleDebugEnabled()) {
    console.warn(...args);
  }
}

/**
 * Conditional console.error - only logs if console debugging is enabled
 */
export function debugError(...args: any[]): void {
  if (isConsoleDebugEnabled()) {
    console.error(...args);
  }
}

/**
 * Component-specific debug log
 */
export function componentDebugLog(componentName: string, ...args: any[]): void {
  if (isConsoleDebugEnabled() && isComponentEnabled(componentName)) {
    console.log(`[${componentName}]`, ...args);
  }
}

/**
 * Update debug configuration at runtime
 * Useful for debugging in production via browser console
 *
 * Example usage in browser console:
 * window.updateDebugConfig({ enableAllLogs: true, enableConsoleDebugging: true })
 */
export function updateDebugConfig(updates: Partial<DebugConfig>): void {
  Object.assign(debugConfig, updates);
  console.log('Debug configuration updated:', debugConfig);
}

/**
 * Get current debug configuration
 */
export function getDebugConfig(): DebugConfig {
  return { ...debugConfig };
}

/**
 * Expose debug utilities to window for runtime debugging
 */
if (typeof window !== 'undefined') {
  (window as any).debugConfig = {
    get: getDebugConfig,
    update: updateDebugConfig,
    enable: () => updateDebugConfig({ enableAllLogs: true, enableConsoleDebugging: true, enableLoggerDebugging: true }),
    disable: () => updateDebugConfig({ enableAllLogs: false, enableConsoleDebugging: false, enableLoggerDebugging: false }),
    enableComponent: (name: string) => {
      debugConfig.components[name] = true;
      console.log(`Debugging enabled for component: ${name}`);
    },
    disableComponent: (name: string) => {
      debugConfig.components[name] = false;
      console.log(`Debugging disabled for component: ${name}`);
    },
    enableCategory: (category: keyof DebugConfig['categories']) => {
      debugConfig.categories[category] = true;
      console.log(`Debugging enabled for category: ${category}`);
    },
    disableCategory: (category: keyof DebugConfig['categories']) => {
      debugConfig.categories[category] = false;
      console.log(`Debugging disabled for category: ${category}`);
    },
  };

  console.log('🔧 Debug utilities available at window.debugConfig');
  console.log('  - window.debugConfig.enable() - Enable all debugging');
  console.log('  - window.debugConfig.disable() - Disable all debugging');
  console.log('  - window.debugConfig.get() - Get current config');
  console.log('  - window.debugConfig.enableComponent("Api") - Enable component debugging');
  console.log('  - window.debugConfig.enableCategory("api") - Enable category debugging');
}

export default debugConfig;
